import fetch from 'cross-fetch';
import 'url-search-params-polyfill';
import { responseChunks } from './streaming';
/**
 * Default transport implementation.
 */
export class HttpTransport {
    constructor({ authorization, apiUrl, method, headers = {}, credentials, fetchTimeout, signal, }) {
        this.authorization = authorization;
        this.apiUrl = apiUrl;
        this.method = method;
        this.headers = headers;
        this.credentials = credentials;
        this.fetchTimeout = fetchTimeout;
        this.signal = signal;
    }
    request(apiMethod, { method, fetchTimeout, baseRequestId, signal, ...params }) {
        let spanCounter = 1;
        const searchParams = new URLSearchParams(params &&
            Object.keys(params)
                .map((k) => ({
                [k]: typeof params[k] === 'object'
                    ? JSON.stringify(params[k])
                    : params[k],
            }))
                .reduce((a, b) => ({ ...a, ...b }), {}));
        let url = `${this.apiUrl}/${apiMethod}${searchParams.toString().length ? `?${searchParams}` : ''}`;
        const requestMethod = method ?? this.method ?? (url.length < 2000 ? 'GET' : 'POST');
        if (requestMethod === 'POST') {
            url = `${this.apiUrl}/${apiMethod}`;
            this.headers['Content-Type'] = 'application/json';
        }
        const effectiveFetchTimeout = fetchTimeout ?? this.fetchTimeout;
        const actualSignal = signal ||
            this.signal ||
            (effectiveFetchTimeout
                ? AbortSignal.timeout(effectiveFetchTimeout)
                : undefined);
        // Currently, all methods make GET requests. If a method makes a request with a body payload,
        // remember to add {'Content-Type': 'application/json'} to the header.
        const runRequest = () => fetch(url, {
            method: requestMethod,
            headers: {
                Authorization: this.authorization,
                'x-request-id': baseRequestId && `${baseRequestId}-span-${spanCounter++}`,
                ...this.headers,
            },
            credentials: this.credentials,
            body: requestMethod === 'POST' ? JSON.stringify(params) : null,
            signal: actualSignal,
        });
        return {
            /* eslint no-unsafe-finally: off */
            async subscribe(callback) {
                try {
                    const result = await runRequest();
                    return callback(result, () => this.subscribe(callback));
                }
                catch (e) {
                    let errorMessage = 'network Error';
                    if (e.name === 'AbortError') {
                        if (actualSignal?.reason === 'TimeoutError' ||
                            actualSignal?.reason?.name === 'TimeoutError') {
                            errorMessage = 'timeout';
                        }
                        else {
                            errorMessage = 'aborted';
                        }
                    }
                    const result = { error: errorMessage };
                    return callback(result, () => this.subscribe(callback));
                }
            },
        };
    }
    requestStream(apiMethod, { method, fetchTimeout, baseRequestId, signal, params }) {
        const processedParams = {};
        // Handle the generic params object
        if (params) {
            Object.keys(params).forEach((k) => {
                const value = params[k];
                if (value !== undefined) {
                    processedParams[k] = typeof value === 'object' ? JSON.stringify(value) : String(value);
                }
            });
        }
        const searchParams = new URLSearchParams(processedParams);
        let url = `${this.apiUrl}/${apiMethod}${searchParams.toString().length ? `?${searchParams}` : ''}`;
        const requestMethod = method ?? this.method ?? 'POST';
        if (requestMethod === 'POST') {
            url = `${this.apiUrl}/${apiMethod}`;
            this.headers['Content-Type'] = 'application/json';
        }
        const effectiveFetchTimeout = fetchTimeout ?? this.fetchTimeout;
        let controller;
        let actualSignal = signal || this.signal;
        if (!actualSignal && effectiveFetchTimeout) {
            controller = new AbortController();
            actualSignal = controller.signal;
            setTimeout(() => controller?.abort(), effectiveFetchTimeout);
        }
        return {
            stream: async () => {
                const response = await fetch(url, {
                    method: requestMethod,
                    headers: {
                        Authorization: this.authorization,
                        'x-request-id': baseRequestId || 'stream-request',
                        ...this.headers,
                    },
                    credentials: this.credentials,
                    body: requestMethod === 'POST' ? JSON.stringify(params || {}) : null,
                    signal: actualSignal,
                });
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
                if (!response.body) {
                    throw new Error('No response body available for streaming');
                }
                return responseChunks(response);
            },
            unsubscribe: async () => {
                if (controller) {
                    controller.abort();
                }
            },
        };
    }
}
export default HttpTransport;
